<template>
  <Modal
    v-if="visible"
    :title="title || 'Choose an image from project storage'"
    :width="640"
  >
    <div class="sip_body">
      <label v-if="pins.length" class="sip_pin_toggle">
        <input type="checkbox" v-model="includePins" />
        Include pins (node / channel labels) on the selected image
      </label>

      <div v-if="loading" class="sip_message">Loading images…</div>
      <div v-else-if="compositing" class="sip_message">Building image with pins…</div>
      <div v-else-if="error" class="sip_message sip_error">{{ error }}</div>
      <div v-else-if="images.length === 0" class="sip_message">
        No images found in this folder.
      </div>
      <div v-else class="sip_grid">
        <div
          v-for="image in images"
          :key="image.name"
          class="sip_item"
          :title="image.filename"
          @click="selectImage(image)"
        >
          <img :src="image.thumbUrl" :alt="image.filename" class="sip_thumb" />
          <div class="sip_caption">{{ image.filename }}</div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <Btn
        class="hol_btn"
        :title="$txt ? $txt.cancel : 'Cancel'"
        @clicked="$emit('close')"
        data-default-esc
      />
    </template>
  </Modal>
</template>

<script>
import { BlobServiceClient } from "@azure/storage-blob";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif"];

export default {
  // eslint-disable-next-line
  name: "StorageImagePicker",
  emits: ["select", "close"],

  props: {
    visible: { type: Boolean, default: false },
    // Virtual-folder prefix within the container, e.g. "ProjectOverview/" or "ZoneOverview/132/"
    prefix: { type: String, required: true },
    accountName: { type: String, required: true },
    containerName: { type: String, required: true },
    sasToken: { type: String, required: true },
    title: { type: String, default: null },
    // Overlay pins for the current scope: [{ x, y, label }] where x/y are percentages (0-100)
    // relative to the image (see project_pin_coordinate_system memory).
    pins: { type: Array, default: () => [] },
  },

  data() {
    return {
      images: [],
      loading: false,
      compositing: false,
      error: null,
      includePins: false,
    };
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.includePins = false;
        this.loadImages();
      }
    },
  },

  methods: {
    async loadImages() {
      this.loading = true;
      this.error = null;
      this.images = [];

      try {
        const blobServiceClient = new BlobServiceClient(
          `https://${this.accountName}.blob.core.windows.net?${this.sasToken}`
        );
        const containerClient = blobServiceClient.getContainerClient(
          this.containerName
        );

        const found = [];
        for await (const blob of containerClient.listBlobsFlat({
          prefix: this.prefix,
        })) {
          const filename = blob.name.split("/").pop();
          if (!this.isImage(filename)) continue;
          found.push({
            name: blob.name, // full blob path within the container
            filename: filename,
            thumbUrl: `https://${this.accountName}.blob.core.windows.net/${this.containerName}/${blob.name}?${this.sasToken}`,
          });
        }
        this.images = found;
      } catch (err) {
        console.error("Error listing storage images:", err);
        this.error = "Could not load images from storage.";
      } finally {
        this.loading = false;
      }
    },

    isImage(filename) {
      const lower = (filename || "").toLowerCase();
      return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
    },

    async selectImage(image) {
      if (this.includePins && this.pins.length) {
        this.compositing = true;
        try {
          const dataUrl = await this.buildComposite(image);
          const filename =
            image.filename.replace(/\.[^.]+$/, "") + "_pins.png";
          this.$emit("select", { name: image.name, filename, dataUrl });
        } catch (err) {
          console.error("Error building composite image:", err);
          this.error = "Could not build the image with pins.";
          return;
        } finally {
          this.compositing = false;
        }
      } else {
        this.$emit("select", {
          name: image.name,
          filename: image.filename,
          dataUrl: null,
        });
      }
    },

    // Fetches the map blob and draws the base image + pin markers/labels onto a
    // canvas, returning a PNG data URL. Uses a blob: object URL (same-origin) so
    // the canvas is never tainted and toDataURL works without cross-origin config.
    async buildComposite(image) {
      const response = await fetch(image.thumbUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image blob: ${image.name}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      try {
        const img = await this.loadImageEl(objectUrl);
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;

        const scaleFactor = 2; // render at 2x for crisper text/markers
        const canvas = document.createElement("canvas");
        canvas.width = w * scaleFactor;
        canvas.height = h * scaleFactor;
        const ctx = canvas.getContext("2d");
        ctx.scale(scaleFactor, scaleFactor);

        ctx.drawImage(img, 0, 0, w, h);
        this.drawPins(ctx, w, h);

        return canvas.toDataURL("image/png");
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    },

    loadImageEl(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },

    drawPins(ctx, w, h) {
      const fontSize = Math.max(12, Math.round(w / 70));
      const radius = Math.max(2, Math.round(w / 450));
      const markerColor = "rgba(217, 35, 31, 0.55)";

      ctx.textBaseline = "middle";
      ctx.font = `${fontSize}px Arial, sans-serif`;
      ctx.lineJoin = "round";

      for (const pin of this.pins) {
        const px = (pin.x / 100) * w;
        const py = (pin.y / 100) * h;

        // marker dot
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fillStyle = markerColor;
        ctx.fill();
        ctx.lineWidth = Math.max(1, radius / 3);
        ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
        ctx.stroke();

        // label with white halo for legibility on any background
        if (pin.label) {
          const tx = px + radius + 3;
          ctx.lineWidth = Math.max(2, fontSize / 5);
          ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
          ctx.strokeText(pin.label, tx, py);
          ctx.fillStyle = "#000000";
          ctx.fillText(pin.label, tx, py);
        }
      }
    },
  },
};
</script>

<style lang="css" scoped>
.sip_body {
  min-height: 120px;
}

.sip_pin_toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 13px;
  cursor: pointer;
}

.sip_message {
  padding: 20px;
  text-align: center;
  color: #666;
}

.sip_error {
  color: #c00;
}

.sip_grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.sip_item {
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px;
  transition: border-color 0.1s ease, box-shadow 0.1s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sip_item:hover {
  border-color: #42b983;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.sip_thumb {
  width: 100%;
  height: 110px;
  object-fit: contain;
  background-color: #f5f5f5;
}

.sip_caption {
  margin-top: 6px;
  font-size: 12px;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
